import { useEffect } from "react"
import AnnotationService from "services/annotations.service"
import { useViewerStore } from "store/viewer.store"
import type { TPageElevationData } from "types/elevationReferences.type"

/**
 * Keeps the on-drawing furniture info labels (REQ-401) in sync with the quotation cache.
 *
 * Deliberately cache-driven rather than wired into each edit call-site: every furniture mutation
 * already writes `["quotation", quotationId]` (via `spliceFurnitureIntoCache` /
 * `reorderFurnitureInCache`, or an invalidate), so a new model number, changed dimensions, a
 * category switch, an "Other" name/price, a status recolor, paste/duplicate and batch delete all
 * reach the labels through this one effect.
 *
 * Pass `undefined` to remove every label — that is how preview mode (which renders no highlights at
 * all) is handled.
 */
export const useFurnitureLabels = (relatedObjects: TPageElevationData[] | undefined) => {
	const instance = useViewerStore((state) => state.instance)

	useEffect(() => {
		if (!instance) return

		const docViewer = instance.Core.documentViewer
		const sync = () => AnnotationService.syncFurnitureLabels(relatedObjects ?? [])

		// Data usually arrives after the document is already loaded (a refetch, or a mutation
		// writing the cache), so sync straight away; the listener covers the first load, where the
		// page dimensions the placement math needs don't exist yet. Registration order relative to
		// `useAnnotations`' own documentLoaded listener is irrelevant — `renderAnnotations` leaves
		// labels alone, so neither ordering can wipe them.
		sync()
		docViewer.addEventListener("documentLoaded", sync)

		return () => {
			docViewer.removeEventListener("documentLoaded", sync)
		}
	}, [instance, relatedObjects])
}
